import { useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Shield,
  Clock,
  FileText,
  CheckCircle
} from "lucide-react";

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const today = new Date();
  const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        {/* Header */}
        <header className="pt-20 pb-12 px-4">
          <div className="max-w-6xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl mb-6 shadow-xl">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Privacy Policy
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Your privacy is important to us. Learn how we collect, use, and protect your information.
            </p>
            <Badge variant="secondary" className="mt-4 bg-blue-100 text-blue-800 border-blue-200 px-4 py-2 text-sm font-medium">
              <Clock className="w-4 h-4 mr-2" />
              Effective Date: {formattedDate}
            </Badge>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-4 py-12 space-y-8">
          {/* Introduction */}
          <Card className="border-0 shadow-2xl bg-white/80 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <FileText className="w-6 h-6 text-blue-600" />
                Timelyyy Privacy Policy
              </CardTitle>
              <CardDescription className="text-gray-600">
                Your privacy and data protection are our top priorities
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed">
                Timelyyy (Hereinafter Referred to as "Platform", "Website", "Timelyyy") is a
                Vehicle Wash Booking platform owned and operated by Timelyyy Technologies
                (Hereinafter Referred to as "Timelyyy", "we", "us", or "our").
              </p>
            </CardContent>
          </Card>
          <p className="text-gray-600 mb-4">
            This Privacy Policy (hereinafter referred to as “Policy”),
            helps the users accessing the Website/App (“User”/ “You”/ “Your”)
            and the Service therein to understand the type of information
            collected, the purpose for which the information is collected; how
            the information is used; the intended recipients of such
            information; and how the information is protected. We take
            protection and proper use of Your information seriously and are
            committed to protecting such information in Our possession. We
            advise You to read this Policy carefully prior to Your access of
            Website/App, use of Services, or creation of an Account on the
            Website/App. If You do not agree to this Policy, please do not
            access Our Website/App, use Our Services, or create an Account in
            Our Website/App.
          </p>
          <p className="text-gray-600 mb-4">
            This Policy is published and shall be construed to be in
            accordance with the provisions of the Information Technology
            (Reasonable Security Practices and Procedures and Sensitive
            Personal Data of Information) Rules, 2011 and Information
            Technology (Intermediaries Guidelines And Digital Media Ethics
            Code) Rules, 2021 under the Information Technology Act, 2000 and
            other applicable laws that require publishing of the privacy
            notice for collection, usage, storage, disclosure and transfer of
            sensitive personal data or information. Any capitalized terms used
            herein without definition shall have the meanings assigned to them
            in Our Terms of Use.
          </p>
          <p className="text-gray-600 mb-4">
            We use your data to provide and improve the Service. By using the
            Service, you agree to the collection and use of information in
            accordance with this policy. Unless otherwise defined in this
            Privacy Policy, the terms used in this Privacy Policy have the
            same meanings as in our Terms and Conditions
          </p>
          <p className="text-gray-600 mb-4">
            The terms of this policy will be effective upon your acceptance of
            the same (directly or indirectly in electronic form, by use of the
            platform and/or by creating a registered account on the platform
            (hereinafter referred to as “account”) and will govern the
            relationship between you and us for your use of the platform and
            the services rendered therein.
          </p>

          {/* Definitions */}
          <section className="bg-white rounded-xl p-8 shadow-sm animate-fade-in">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
              <CheckCircle className="text-green-600 mr-2" />
              Definitions
            </h2>
            <dl className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  term: "Service",
                  definition:
                    "The doubetick.io website and the ChatTab mobile application operated by Nexify Infotech LLP",
                },
                {
                  term: "Personal Data",
                  definition:
                    "Data about a living individual who can be identified from those data",
                },
                {
                  term: "Usage Data",
                  definition:
                    "Data collected automatically generated by the use of the Service or from the Service infrastructure itself (for example, the duration of a page visit).",
                },
                {
                  term: "Cookies",
                  definition:
                    "Small files stored on your device (computer or mobile device)",
                },
                {
                  term: "Data Controller",
                  definition:
                    "Natural or legal person who determines the purposes for which and the manner in which any personal information are, or are to be, processed. For the purpose of this Privacy Policy, we are a Data Controller of your Personal Data.",
                },
                {
                  term: "Data Processors",
                  definition:
                    "Natural or legal person who processes the data on behalf of the Data Controller.We may use the services of various Service Providers in order to process your data more effectively.",
                },
                {
                  term: "Data Subject",
                  definition:
                    "Any living individual using our Service and subject of Personal Data",
                },
              ].map((item) => (
                <div key={item.term} className="p-4 bg-green-50 rounded-lg">
                  <dt className="font-medium text-green-600">{item.term}</dt>
                  <dd className="text-gray-600 mt-1">{item.definition}</dd>
                </div>
              ))}
            </dl>
          </section>

          {/* Data Collection Section */}
          <section className="bg-white rounded-xl p-8 shadow-sm animate-fade-in">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
              <FileText className="text-green-600 mr-2" />
              Information Collection and Use
            </h2>

            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Types of Data Collected
                </h3>
                <div className="ml-4 space-y-6">
                  <div>
                    <h4 className="font-medium text-gray-800 mb-3">
                      Personal Data
                    </h4>
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      While using our Service, we may ask you to provide us with
                      certain personally identifiable information that can be
                      used to contact or identify you ("Personal Data").
                      Personally identifiable information may include, but is
                      not limited to:
                    </p>
                    <ul className="list-disc pl-6 text-gray-600 space-y-2">
                      {[
                        "Email address",
                        "First name and last name",
                        "Phone number associated with WhatsApp",
                        "Address, State, Province, ZIP/Postal code, City",
                        "Cookies and Usage Data",
                        "Social Media login credentials",
                        "Any other identifiers and any other sensitive/personal information as defined under the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data of Information) Rules, 2011 as amended from time to time and such other data privacy laws whether now in force or in future (“Personal Data”).",
                      ].map((item) => (
                        <li key={item} className="leading-relaxed">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <p className="text-gray-600 mb-4 leading-relaxed">
                    We may use your Personal Data to contact you with phone
                    calls, newsletters, sms, marketing or promotional materials
                    and other information that may be of interest to you. And
                    irrespective of the fact that if also you have registered
                    yourself under DND or DNC or NCPR Service, you still
                    authorize us to give you a call for the above mentioned
                    purposes till you have an account with us. You may opt out
                    of receiving any, or all, of these communications from us by
                    following the unsubscribe link or the instructions provided
                    in any email we send.
                  </p>

                  <div>
                    <h4 className="font-medium text-gray-800 mb-3">
                      Usage Data
                    </h4>
                    <p className="text-gray-600 leading-relaxed">
                      We may also collect information that your browser sends
                      whenever you visit our Service or when you access the
                      Service by or through a mobile device ("Usage Data").
                    </p>
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      This Usage Data may include information such as your
                      computer's Internet Protocol address (e.g. IP address),
                      browser type, browser version, the pages of our Service
                      that you visit, the time and date of your visit, the time
                      spent on those pages, unique device identifiers and other
                      diagnostic data.
                    </p>
                    <p className="text-gray-600 leading-relaxed">
                      When you access the Service with a mobile device, this
                      Usage Data may include information such as the type of
                      mobile device you use, your mobile device unique ID, the
                      IP address of your mobile device, your mobile operating
                      system, the type of mobile Internet browser you use,
                      unique device identifiers and other diagnostic data.
                    </p>
                  </div>

                  {/* Other Data Types */}
                  {[
                    {
                      title: "Behavioral Data",
                      content:
                        "We shall collect information which is connected with Your activity on the Website/App across all Your devices using Your email or social media log-in details, as well as Services that You search, and opt-ins and communication preferences.",
                    },
                    {
                      title: "Location Data",
                      content:
                        'We may use and store information about your location if you give us permission to do so ("Location Data"). We use this data to provide features of our Service, to improve and customise our Service. You can enable or disable location services when you use our Service at any time by way of your device settings.',
                    },
                    {
                      title: "Contact List & Files",
                      content:
                        "We shall allow you to upload your existing contact list that contains name, mobile number, email and other data fields to initiate conversations from our Website/App. We collect the information related to your device audio, video and other rich media files such as PDF documents, and enable you to share rich media files to your customers through our Website/App.",
                    },
                    {
                      title: "Tracking & Cookies",
                      content:
                        "We use cookies and similar tracking technologies to track the activity on our Service and we hold certain information. Cookies are files with a small amount of data which may include an anonymous unique identifier. Cookies are sent to your browser from a website and stored on your device. Other tracking technologies are also used such as beacons, tags and scripts to collect and track information and to improve and analyse our Service. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our Service. Examples of Cookies we use: • Session Cookies. We use Session Cookies to operate our Service. • Preference Cookies. We use Preference Cookies to remember your preferences and various settings. • Security Cookies. We use Security Cookies for security purposes.",
                    },
                    {
                      title: "Device Data",
                      content:
                        "We collect information about computers, phones, and other connected devices with a web connection and the presence of Our Website/App and Services and integrate this information with the various devices You use. This information includes: device features such as information about operating system, hardware and software versions, battery status, signal availability, available storage space, browser type, app or file names and types, and plug-ins, device activities such as information about the activity and behavior of the device, for example in the background or in front of a window or with mouse movements (which help to distinguish humans from boats), unique identifiers, device ID, games, apps, or accounts You use, Bluetooth signals and information about nearby WiFi access points, beacons and cell towers, Information that We are permitted to collect by turning on access in device configuration, as well as providing access to GPS location, camera or photo, information such as Your mobile operator name or ISP, language, time zone, mobile phone number, IP address, connection speed, and other devices near Your device or on the network so that We can help You with the streaming of the Services on Your device.",
                    },
                    {
                      title: "Third Party Information",
                      content:
                        "Information about You that We may receive from third parties services, such as advertising partners, data providers and analytics providers. We may collect Your third-party social media networks information including Your contact lists for these services and information relating to your use of the Website/App in relation to these services, if You choose to link or sign up using a third-party social network or login service (such as Facebook, Twitter, Instagram, or Google). If you link your Account in our Website/App to another service, We may receive information about Your use of that service.",
                    },
                  ].map((item) => (
                    <div
                      key={item.title}
                      className="border-l-4 border-green-100 pl-4"
                    >
                      <h4 className="font-medium text-gray-800 mb-2">
                        {item.title}
                      </h4>
                      <p className="text-gray-600 leading-relaxed">
                        {item.content}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Data Usage Section */}
          <section className="bg-white rounded-xl p-8 shadow-sm animate-fade-in">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
              <Shield className="text-green-600 mr-2" />
              Use of Data
            </h2>
            <p className="text-gray-600 mb-6">
              Nexify Infotech LLP uses the collected data for various purposes:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
              {[
                "To provide and maintain our Service",
                "To notify you about changes to our Service",
                "To allow you to participate in interactive features of our Service when you choose to do so",
                "To provide customer support",
                "To gather analysis or valuable information so that we can improve our Service",
                "To monitor the usage of our Service",
                "To detect, prevent and address technical issues",
                "To provide you with news, special offers, and general information about other goods, services, and events which we offer that are similar to those that you have already purchased or enquired about unless you have opted not to receive such information",
                "However, Data from Google Sheets will not be shared with any third-party tools.",
              ].map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <Shield className="text-green-600 mr-2" />
              Legal Basis for Processing Personal Data under GDPR
            </h3>
            <p className="text-gray-600 mb-4">
              If you are from the European Economic Area (EEA), Nexify Infotech
              LLP's legal basis for collecting and using the personal
              information described in this Privacy Policy depends on the
              Personal Data we collect and the specific context in which we
              collect it.
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
              {[
                "We need to perform a contract with you",
                "You have given us permission to do so",
                "The processing is in our legitimate interests and it is not overridden by your rights",
                "For payment processing purposes",
                "To comply with the law",
              ].map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <Clock className="text-green-600 mr-2" />
              Retention of Data
            </h3>
            <p className="text-gray-600 mb-4">
              Nexify Infotech LLP will retain your Personal Data only for as
              long as is necessary for the purposes set out in this Privacy
              Policy. We will retain and use your Personal Data to the extent
              necessary to comply with our legal obligations (for example, if we
              are required to retain your data to comply with applicable laws),
              resolve disputes, and enforce our legal agreements and policies.
            </p>
            <p className="text-gray-600 mb-6">
              Nexify Infotech LLP will also retain Usage Data for internal
              analysis purposes. Usage Data is generally retained for a shorter
              period of time, except when this data is used to strengthen the
              security or to improve the functionality of our Service, or we are
              legally obligated to retain this data for longer periods.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <Shield className="text-green-600 mr-2" />
              Transfer of Data
            </h3>
            <p className="text-gray-600 mb-4">
              Your information, including Personal Data, may be transferred to —
              and maintained on — computers located outside of your state,
              province, country, or other governmental jurisdiction where the
              data protection laws may differ from those of your jurisdiction.
            </p>
            <p className="text-gray-600 mb-4">
              If you are located outside India and choose to provide information
              to us, please note that we transfer the data, including Personal
              Data, to India and process it there.
            </p>
            <p className="text-gray-600 mb-6">
              Your consent to this Privacy Policy followed by your submission of
              such information represents your agreement to that transfer.
              Nexify Infotech LLP will take all the steps reasonably necessary
              to ensure that your data is treated securely and in accordance
              with this Privacy Policy, and no transfer of your Personal Data
              will take place to an organisation or a country unless there are
              adequate controls in place, including the security of your data
              and other personal information.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <CheckCircle className="text-green-600 mr-2" />
              Disclosure of Data
            </h3>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">
              Business Transaction
            </h4>
            <p className="text-gray-600 mb-4">
              If Nexify Infotech LLP is involved in a merger, acquisition, or
              asset sale, your Personal Data may be transferred.
            </p>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">
              Disclosure for Law Enforcement
            </h4>
            <p className="text-gray-600 mb-6">
              Under certain circumstances, Nexify Infotech LLP may be required
              to disclose your Personal Data if required to do so by law or in
              response to valid requests by public authorities (e.g., a court or
              a government agency).
            </p>
          </section>

          {/* Legal & Security Sections */}
          <div className="max-w-7xl mx-auto px-4">
            <section className="bg-white rounded-xl p-8 shadow-sm animate-fade-in mb-8 w-full">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                <Shield className="text-green-600 mr-2" />
                Security of Data
              </h2>
              <p className="text-gray-600">
                The security of your data is important to us but remember that
                no method of transmission over the Internet or method of
                electronic storage is 100% secure. While we strive to use
                commercially acceptable means to protect your Personal Data, we
                cannot guarantee its absolute security.
              </p>
            </section>

            <section className="bg-white rounded-xl p-8 shadow-sm animate-fade-in w-full">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                <CheckCircle className="text-green-600 mr-2" />
                Your Data Protection Rights under GDPR
              </h2>
              <p className="text-gray-600 mb-4">
                If you are a resident of the European Economic Area (EEA), you
                have certain data protection rights. Nexify Infotech LLP aims to
                take reasonable steps to allow you to correct, amend, delete, or
                limit the use of your Personal Data.
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>
                  The right to access, update, or delete the information we have
                  on you. Whenever made possible, you can access, update, or
                  request deletion of your Personal Data directly within your
                  account settings section. If you are unable to perform these
                  actions yourself, please contact us to assist you.
                </li>
                <li>
                  The right of rectification. You have the right to have your
                  information rectified if that information is inaccurate or
                  incomplete.
                </li>
                <li>
                  The right to object. You have the right to object to our
                  processing of your Personal Data.
                </li>
                <li>
                  The right of restriction. You have the right to request that
                  we restrict the processing of your personal information.
                </li>
                <li>
                  The right to data portability. You have the right to be
                  provided with a copy of the information we have on you in a
                  structured, machine-readable, and commonly used format.
                </li>
                <li>
                  The right to withdraw consent. You also have the right to
                  withdraw your consent at any time where Nexify Infotech LLP
                  relied on your consent to process your personal information.
                </li>
              </ul>
              <p className="text-gray-600 mt-4">
                Please note that we may ask you to verify your identity before
                responding to such requests.
              </p>
            </section>
          </div>

          {/* Service Providers */}
          <section className="bg-white rounded-xl p-8 shadow-sm animate-fade-in">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">
              Service Providers
            </h2>
            <p className="text-gray-600 mb-6">
              We may employ third-party companies and individuals to facilitate
              our Service ("Service Providers"), provide the Service on our
              behalf, perform Service-related services, or assist us in
              analyzing how our Service is used. These third parties have access
              to your Personal Data only to perform these tasks on our behalf
              and are obligated not to disclose or use it for any other purpose.
            </p>

            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Analytics
                </h3>
                <div className="space-y-4">
                  {[
                    {
                      name: "Google Analytics",
                      description:
                        "Google Analytics is a web analytics service offered by Google that tracks and reports website traffic. Google uses the data collected to track and monitor the use of our Service. This data is shared with other Google services. Google may use the collected data to contextualize and personalize the ads of its own advertising network.",
                      policy: "https://policies.google.com/privacy",
                      optout: "https://tools.google.com/dlpage/gaoptout",
                    },
                    {
                      name: "Firebase",
                      description:
                        "Firebase is an analytics service provided by Google Inc. You may opt-out of certain Firebase features through your mobile device settings, such as your device advertising settings or by following the instructions provided by Google in their Privacy Policy.",
                      policy: "https://policies.google.com/privacy",
                      optout: "Device settings",
                    },
                    {
                      name: "Mixpanel",
                      description:
                        "Mixpanel is provided by Mixpanel Inc. You can prevent Mixpanel from using your information for analytics purposes by opting-out. To opt-out of the Mixpanel service, please visit this page.",
                      policy: "https://mixpanel.com/terms/",
                      optout: "https://mixpanel.com/optout/",
                    },
                  ].map((service) => (
                    <div
                      key={service.name}
                      className="border-l-4 border-green-100 pl-4"
                    >
                      <h4 className="font-medium text-gray-800">
                        {service.name}
                      </h4>
                      <p className="text-gray-600 text-sm mb-2">
                        {service.description}
                      </p>
                      <div className="text-gray-600 text-sm">
                        <a
                          href={service.policy}
                          className="text-green-600 hover:underline"
                        >
                          Privacy Policy
                        </a>
                        {service.optout && (
                          <span>
                            {" "}
                            •{" "}
                            <a
                              href={service.optout}
                              className="text-green-600 hover:underline"
                            >
                              Opt-out
                            </a>
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Payments
                </h3>
                <p className="text-gray-600 mb-4">
                  We may provide paid products and/or services within the
                  Service. In that case, we use third-party services for payment
                  processing (e.g., payment processors). We will not store or
                  collect your payment card details. That information is
                  provided directly to our third-party payment processors whose
                  use of your personal information is governed by their Privacy
                  Policy.
                </p>
                <div className="grid gap-4 md:grid-cols-2">
                  {[
                    {
                      name: "Apple Store In-App Payments",
                      url: "https://www.apple.com/legal/privacy/en-ww/",
                    },
                    {
                      name: "Google Play In-App Payments",
                      url: "https://www.google.com/policies/privacy/",
                    },
                    { name: "Stripe", url: "https://stripe.com/us/privacy" },
                    { name: "Razorpay", url: "https://razorpay.com/privacy/" },
                  ].map((processor) => (
                    <a
                      key={processor.name}
                      href={processor.url}
                      className="p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                    >
                      <div className="font-medium text-gray-800">
                        {processor.name}
                      </div>
                      <div className="text-sm text-green-600">
                        Privacy Policy →
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Other Sections */}
          {[
            {
              title: "Refund Policy",
              icon: CheckCircle,
              content:
                "Nexify Infotech LLP (”Company”) charges and collects in advance for the use of the SaaS services (ChatTab). All services rendered are non-refundable. All monthly, quarterly, yearly, and multi-year subscriptions renew automatically on their due renewal date according to the date of purchase until officially cancelled in writing. Customers may cancel anytime by emailing a notice to support@chattab.in. Important: No refunds or credits for partial months or years of service will be refunded to a customer upon cancellation. In case the amount remains outstanding for more than fifteen (15) days from the date of activation of services (for first-time users) & more than one (1) day for auto-renewal cases, the company reserves the right to cancel the subscription.",
            },
            {
              title: "Children's Privacy",
              icon: CheckCircle,
              content:
                'Our Service does not address anyone under the age of 18 ("Children"). We do not knowingly collect personally identifiable information from anyone under the age of 18. If you are a parent or guardian and you are aware that your Child has provided us with Personal Data, please contact us. If we become aware that we have collected Personal Data from children without verification of parental consent, we take steps to remove that information from our servers.',
            },
            {
              title: "Changes to This Privacy Policy",
              icon: CheckCircle,
              content:
                'We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page. We will let you know via email and/or a prominent notice on our Service, prior to the change becoming effective and update the "effective date" at the top of this Privacy Policy. You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.',
            },
          ].map((section) => (
            <section
              key={section.title}
              className="bg-white rounded-xl p-8 shadow-sm animate-fade-in"
            >
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                <section.icon className="text-green-600 mr-2" />
                {section.title}
              </h2>
              <p className="text-gray-600">{section.content}</p>
            </section>
          ))}

          {/* Contact Section */}
          <section className="text-center py-8 animate-fade-in">
            <div className="bg-green-50 rounded-xl p-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                Contact Us
              </h2>
              <p className="text-gray-600 mb-4">
                If you have any questions or suggestions about our Privacy
                Policy, do not hesitate to contact us.
              </p>
              <a
                href="mailto:support@chattab.in"
                className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <CheckCircle className="mr-2" />
                support@chattab.in
              </a>
            </div>
          </section>
        </main>

      </div>
  );
};

export default PrivacyPolicy;
